/**
 * <kaffir-drawer> — off-canvas navigation drawer.
 *
 * Deliberately a separate custom element from Dawn's <header-drawer>: that class
 * still lives in global.js and drives the mobile facets drawer, and re-defining
 * the tag would throw. Nothing here depends on --header-bottom-position or
 * --viewport-height; the panel is fixed to the viewport.
 */
class KaffirDrawer extends HTMLElement {
  constructor() {
    super();

    this.onToggleClick = this.onToggleClick.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onBreakpointChange = this.onBreakpointChange.bind(this);

    // Above this width the hamburger is hidden, so an open drawer would be
    // orphaned. 'desktop' means the merchant chose a drawer menu at every width.
    this.mediaQuery = this.dataset.breakpoint === 'desktop' ? null : window.matchMedia('(min-width: 990px)');
  }

  connectedCallback() {
    // Re-registered on every connect so the drawer keeps working if it is moved
    // in the DOM. The handlers are stable bound references, so this is a no-op
    // when they are already attached.
    document.addEventListener('keydown', this.onKeyDown);
    this.mediaQuery?.addEventListener('change', this.onBreakpointChange);

    // Query children here, not in the constructor: a custom element has no
    // children yet when it is created programmatically (theme editor section
    // re-renders do exactly that).
    if (this.bound) return;
    this.bound = true;

    this.panel = this.querySelector('.kdrawer');
    this.overlay = this.querySelector('.kdrawer__overlay');
    this.toggleButton = this.querySelector('.kdrawer__toggle');
    this.closeButton = this.querySelector('.kdrawer__close');

    if (!this.panel || !this.toggleButton) return;

    this.toggleButton.addEventListener('click', this.onToggleClick);
    this.closeButton?.addEventListener('click', () => this.close());
    this.overlay?.addEventListener('click', () => this.close());

    this.querySelectorAll('.kdrawer__link--parent').forEach((button) => {
      button.addEventListener('click', () => this.toggleSubmenu(button));
    });

    // Close when navigating to an in-page anchor, otherwise the drawer stays
    // over the target the visitor just asked to see.
    this.querySelectorAll('.kdrawer__nav a[href^="#"]').forEach((anchor) => {
      anchor.addEventListener('click', () => this.close());
    });
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onKeyDown);
    this.mediaQuery?.removeEventListener('change', this.onBreakpointChange);
    if (this.isOpen) this.unlockScroll();
  }

  get isOpen() {
    return this.hasAttribute('open');
  }

  onToggleClick(event) {
    event.preventDefault();
    this.isOpen ? this.close() : this.open();
  }

  onKeyDown(event) {
    if (event.key !== 'Escape' || !this.isOpen) return;
    event.stopPropagation();
    this.close();
  }

  onBreakpointChange(event) {
    if (event.matches && this.isOpen) this.close();
  }

  open() {
    if (this.isOpen || !this.panel) return;

    this.setAttribute('open', '');
    this.toggleButton?.setAttribute('aria-expanded', 'true');
    this.lockScroll();

    // Wait for the panel to become visible before moving focus into it.
    requestAnimationFrame(() => {
      if (typeof trapFocus === 'function') {
        trapFocus(this.panel, this.closeButton || this.panel);
      } else {
        this.closeButton?.focus();
      }
    });
  }

  close() {
    if (!this.isOpen) return;

    this.removeAttribute('open');
    this.toggleButton?.setAttribute('aria-expanded', 'false');
    this.unlockScroll();

    // Dawn's removeTrapFocus() calls focus() without preventScroll, and the
    // toggle sits at the top of a non-sticky header — letting it scroll into
    // view would undo the scroll position we just restored.
    if (typeof removeTrapFocus === 'function') removeTrapFocus();
    this.toggleButton?.focus({ preventScroll: true });
  }

  toggleSubmenu(button) {
    const expanded = button.getAttribute('aria-expanded') === 'true';
    button.setAttribute('aria-expanded', (!expanded).toString());
  }

  /**
   * `overflow: hidden` on the root drops the scroll offset to 0 in Chromium, so
   * the page visibly jumps to the top behind the scrim and stays there after the
   * drawer closes. Pin the body at its current offset instead and restore it.
   */
  lockScroll() {
    this.scrollPosition = window.scrollY;

    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    if (scrollbarWidth > 0) document.body.style.paddingRight = `${scrollbarWidth}px`;

    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.scrollPosition}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';

    document.documentElement.classList.add('kdrawer-open');

    // localization-form.js reads this class to decide whether it needs to lock
    // scrolling itself when the country selector opens inside the drawer.
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  unlockScroll() {
    document.documentElement.classList.remove('kdrawer-open');
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);

    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.paddingRight = '';

    // Read a layout property first: without the forced reflow the document is
    // still at its collapsed height and scrollTo clamps to 0.
    void document.body.offsetHeight;
    window.scrollTo(0, this.scrollPosition || 0);
  }
}

if (!customElements.get('kaffir-drawer')) {
  customElements.define('kaffir-drawer', KaffirDrawer);
}
